import React from "react";
import { DOC_CENTER } from "@/constants/testIds";
import { Link2, GitCommit, Zap, Ship, Building2, LayoutGrid, ArrowUpRight } from "lucide-react";

const CARDS = [
  {
    slug: "every-document-connected",
    title: "Every Document Connected",
    desc: "Every PO, invoice, packing list and BoL is automatically tied to the shipment, supplier and container it belongs to — never floats alone.",
    icon: Link2,
    span: "lg:col-span-2",
    hero: true,
  },
  { slug: "version-control", title: "Version Control", desc: "Every revision is preserved. Approved versions are tagged. Old PDFs never get attached again.", icon: GitCommit },
  { slug: "instant-search", title: "Instant Search", desc: "Search by shipment ID, container, supplier, PO or doc type — results in milliseconds.", icon: Zap },
  { slug: "shipment-based-organization", title: "Shipment-Based Organization", desc: "Documents organise themselves around the operation, not around folders or drives.", icon: Ship },
  { slug: "supplier-based-organization", title: "Supplier-Based Organization", desc: "Pull the entire paper trail for any supplier across every shipment in seconds.", icon: Building2 },
  { slug: "one-workspace", title: "One Workspace", desc: "Replaces inboxes, drives, WhatsApp groups and folder structures with one source of truth.", icon: LayoutGrid },
];

export default function WhyDocumentCenter() {
  return (
    <section id="why" data-testid={DOC_CENTER.why.section} className="relative py-28 md:py-36">
      <div className="dc-container">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="dc-eyebrow">Why Document Center™</span>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tighter text-ink-950 sm:text-5xl md:text-[56px]">
              Built for import operations,<br />
              not file management.
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-ink-600">
            Six capabilities that turn scattered paperwork into a connected operational layer of your
            Workspace.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <article
                key={c.slug}
                data-testid={DOC_CENTER.why.card(c.slug)}
                className={`dc-card dc-card-hover group relative overflow-hidden p-6 ${c.span || ""} ${
                  c.hero ? "lg:row-span-2 lg:min-h-[360px]" : ""
                }`}
              >
                {c.hero && (
                  <div className="pointer-events-none absolute inset-0 dc-grid-bg opacity-60" />
                )}
                <div className="relative flex h-full flex-col">
                  <div className="flex items-center justify-between">
                    <div className="grid h-10 w-10 place-items-center rounded-lg border border-[color:var(--dc-border)] bg-white text-ink-900">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-[10.5px] uppercase tracking-wider text-ink-400">
                      0{i + 1}
                    </span>
                  </div>

                  <h3 className={`mt-6 font-display ${c.hero ? "text-3xl md:text-4xl" : "text-xl"} font-extrabold tracking-tight text-ink-950`}>
                    {c.title}
                  </h3>
                  <p className={`mt-3 max-w-md text-[14px] leading-relaxed text-ink-600 ${c.hero ? "md:text-[15.5px]" : ""}`}>
                    {c.desc}
                  </p>

                  {c.hero && (
                    <div className="mt-auto pt-6">
                      <div className="flex flex-wrap gap-2">
                        {["Shipment", "Purchase Order", "Supplier", "Container", "Version"].map((t) => (
                          <span key={t} className="dc-tag bg-white">
                            <Link2 className="h-3 w-3" strokeWidth={1.75} />
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-5 flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-700 opacity-0 transition-all duration-200 group-hover:opacity-100">
                    Learn more <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
